import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import {
  TableContainer,
  Table,
  TableBody,
} from '../../../../components/customTableComponents';
import Head from './components/Head/Head';
import Row from './components/Row/Row';
import NoDataRow from './components/NoDataRow/NoDataRow';
import TableRowsSkeleton from '../skeletons/TableRowsSkeleton/TableRowsSkeleton';
import { columns } from './constants/columns';
import useRequestsQueryState from '../../hooks/useRequestsQueryState';
import { CompleteModal } from '../../../../components/requestComponents';
import useFilterState from '../../hooks/useFilterState';
import { filtersData } from '../../constants/filtersData';
import { filterNames } from '../../constants/filterNames';
import { getRequestsKeys } from '../../queries/queryKeys';
import { queryKeys } from '../../../../queries/queryKeys';
import { InvoiceManager } from '../../../../components/requestComponents';

const { RequestInvoiceModalProvider } = InvoiceManager;

const useSetInvoiceSuccess = () => {
  const queryClient = useQueryClient();

  const [[fromDate, toDate]] = useFilterState(
    filtersData[filterNames.createDate]
  );

  const onSuccess = useCallback(
    (updatedRequest) => {
      queryClient.setQueryData(
        getRequestsKeys(fromDate, toDate),
        (oldRequests) =>
          oldRequests.map((oldRequest) =>
            oldRequest.id === updatedRequest.id ? updatedRequest : oldRequest
          )
      );
    },
    [queryClient, fromDate, toDate]
  );

  return onSuccess;
};

const RequestsTable = ({
  requests,
  onSelectAll,
  onSelect,
  selectedRequests,
  rowCount,
  onSort,
  onExtraSort,
  sortConfig,
  tableClassname,
  showInLifeCirle,
  ...props
}) => {
  const [completedRequestId, setCompletedRequestId] = useState(undefined);

  const requestsQueryState = useRequestsQueryState();

  const queryClient = useQueryClient();

  const [[fromDate, toDate]] = useFilterState(
    filtersData[filterNames.createDate]
  );

  const isCounting = useSelector((state) => state.statisticsStore.isCounting);

  const handleSetInvoiceSuccess = useSetInvoiceSuccess();

  const handleCompletedStatusClick = (request) => {
    setCompletedRequestId(request.id);
  };

  const handleCompleteModalCancel = () => {
    setCompletedRequestId(undefined);
  };

  const handleAfterCompleteModalSubmit = ({ updatedRequest, formValues }) => {
    setCompletedRequestId(undefined);

    if (updatedRequest) {
      queryClient.setQueryData(
        getRequestsKeys(fromDate, toDate),
        (oldRequests) => {
          return oldRequests.map((oldRequest) =>
            oldRequest.id === updatedRequest.id ? updatedRequest : oldRequest
          );
        }
      );

      if (formValues.note.trim()) {
        queryClient.invalidateQueries(
          queryKeys.requestComments(updatedRequest.id)
        );
      }

      if (formValues.files.length) {
        queryClient.invalidateQueries(
          queryKeys.requestAttachments(updatedRequest.id)
        );
      }
    }
  };

  return (
    <>
      <RequestInvoiceModalProvider onSuccess={handleSetInvoiceSuccess}>
        <TableContainer {...props}>
          <Table className={tableClassname}>
            <Head
              handleSort={onSort}
              onExtraSort={onExtraSort}
              sortConfig={sortConfig}
              onSelectAllClick={onSelectAll}
              numSelected={selectedRequests.length}
              rowCount={rowCount}
              checkboxDisabled={requestsQueryState?.isFetching}
              showInLifeCirle={showInLifeCirle}
            />
            <TableBody>
              {requestsQueryState?.isFetching || isCounting ? (
                <TableRowsSkeleton pageSize={10} />
              ) : requests?.length ? (
                requests.map((request) => (
                  <Row
                    key={request.id}
                    request={request}
                    onSelectRowClick={onSelect}
                    selectedRows={selectedRequests}
                    onCompletedStatusClick={handleCompletedStatusClick}
                    showInLifeCirle={showInLifeCirle}
                  />
                ))
              ) : (
                <NoDataRow columnCount={columns.length + 1} />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </RequestInvoiceModalProvider>
      <CompleteModal
        visible={Boolean(completedRequestId)}
        requestId={completedRequestId}
        onCancel={handleCompleteModalCancel}
        afterSubmit={handleAfterCompleteModalSubmit}
      />
    </>
  );
};

export default RequestsTable;
